import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Primitives";
import { InnerHero, CTABand } from "@/components/Sections";
import { IMG, EXPERIENCES, FEATURED_STAYS } from "@/data/site";

export default function Experiences() {
  return (
    <>
      <InnerHero
        overline="Experiences"
        title="Ways to feel India"
        subtitle="A curated collection of premium, relaxation-led experiences — from Ayurveda and yoga to houseboats, mountains and boutique living."
        image={IMG.ayurvedaSpa}
      />
      <section className="bg-ivory py-20 md:py-28" data-testid="experiences-grid">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPERIENCES.map((e, i) => (
              <Reveal key={e.title} delay={(i % 3) * 0.08}>
                <div className="group h-full rounded-2xl border border-sand bg-coconut overflow-hidden" data-testid={`experience-card-${i}`}>
                  <div className="h-56 overflow-hidden">
                    <img src={e.img} alt={e.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-2xl text-forest">{e.title}</h3>
                    <p className="mt-2 text-sm text-navy/65 font-light leading-relaxed">{e.desc}</p>
                    <Link to="/contact" className="mt-5 inline-flex items-center gap-2 text-teal font-semibold text-sm hover:gap-3 transition-all" data-testid={`design-journey-${i}`}>
                      Design This Journey <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section id="stays" className="bg-sand/30 py-20 md:py-28 scroll-mt-24" data-testid="stays-grid">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <p className="overline text-gold">Handpicked Stays</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest">Kerala and South India, beautifully rooted</h2>
            <p className="mt-5 text-lg font-light text-navy/65 leading-relaxed">
              A considered collection of beach, backwater, wildlife, mountain and heritage resorts that feel unmistakably of their place.
            </p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURED_STAYS.map((stay, i) => (
              <Reveal key={stay.name} delay={(i % 3) * 0.06}>
                <div className="group h-full rounded-2xl border border-sand bg-coconut overflow-hidden">
                  <div className="h-60 overflow-hidden">
                    <img src={stay.img} alt={stay.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="overline text-gold">{stay.location}</p>
                    <h3 className="mt-3 font-serif text-2xl text-forest">{stay.name}</h3>
                    <p className="mt-2 text-sm text-navy/60">{stay.style}</p>
                    <Link to="/contact" className="mt-5 inline-flex items-center gap-2 text-teal font-semibold text-sm hover:gap-3 transition-all">
                      Add to My Journey <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTABand
        overline="Design a journey"
        title="Craft the perfect India experience"
        subtitle="Tell us your travellers' pace and taste — we'll shape a curated, premium itinerary around it."
        primary={{ label: "Start Planning", to: "/contact" }}
        secondary={{ label: "See Destinations", to: "/destinations" }}
      />
    </>
  );
}
